import React, { memo, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import { toCamel } from "utils";
import { InfiniteScroll } from "components";
import { LangConstant, SystemConstant } from "const";
import LineChat from "../LineChat";
import { useTranslation } from "react-i18next";
import { getInteractor } from "services/local.service";
import { ConversationSelectors } from "redux-store";
import {
  CHAT_WRAPPER_ID,
  handleDisappearMsg,
  handleNewMessageList,
  isLocalMessage,
  refactorMessage,
} from "../ViewMode.helper";
import isEqual from "lodash/isEqual";
import MessageItem from "../MessageItem";
import { useCleanUpEffect } from "hooks";
import { useConversationContext } from "../ConversationContext";
import { createSelector } from "reselect";
import ScrollTooltip from "components/ScrollTooltip";
import useQueryMessage from "../useQueryMessage";
import useDisappearMessage from "../useDisappearMessage";

const memoizedReduxState = createSelector(
  [
    ConversationSelectors.getSelectedGroupId,
    ConversationSelectors.getThreadingId,
    state => state.conversationRedux.scrollToChildId,
    state => state.conversationRedux.incomingMessages,
  ],
  (selectedGroupId, selectedThreadId, scrollToChildId, incomingMessages) => {
    return { selectedGroupId, selectedThreadId, scrollToChildId, incomingMessages: incomingMessages[selectedThreadId] };
  },
);

const ThreadViewMode = () => {
  const classes = useStyles();
  const { t: getLabel } = useTranslation(LangConstant.NS_HOME);
  const { isMounted } = useCleanUpEffect();
  const { groupDetail } = useConversationContext();

  const { selectedGroupId, selectedThreadId, scrollToChildId, incomingMessages } = useSelector(memoizedReduxState);
  const {
    showMessage,
    isLoadMore,
    scrollDataRef,
    messageListRef,
    getNewerMessage,
    getOlderMessage,
    setMessageList,
    scrollToLastMessage,
  } = useQueryMessage({ selectedGroupId, selectedThreadId, scrollToChildId });
  const { disappearListId } = useDisappearMessage({
    onCallback: disappearList => {
      const newMessageList = handleDisappearMsg(messageListRef.current, disappearList);
      setMessageList(newMessageList);
    },
  });

  const deleteOnlyMeMsgRedux = useSelector(ConversationSelectors.getDeleteOnlyMeMsg);

  const [parentMessage, setParentMessage] = useState({});

  const handleGetParentMessage = async () => {
    const parentMsg = await getInteractor().LocalMessageService.findOne({
      source_id: selectedThreadId,
      send_type: SystemConstant.NORMAL_MSG_SEND_TYPE,
      state: SystemConstant.STATE.active,
    });
    if (!(selectedThreadId && parentMsg?.id)) return;

    // Set parent message
    let displayParentMsg = await refactorMessage(toCamel(parentMsg));
    displayParentMsg = { ...displayParentMsg, isAvatar: true };
    if (!isEqual(displayParentMsg, parentMessage) && isMounted()) setParentMessage(displayParentMsg);
  };

  const handleIncomingMessage = useCallback(async (incomingMessages, selectedGroupId, selectedThreadId) => {
    const isValid = incomingMessages.length > 0 && scrollDataRef.current.isNewest;
    if (isValid) {
      const newMessageList = await handleNewMessageList(incomingMessages, messageListRef.current, selectedGroupId, {
        selectedThreadId,
      });
      setMessageList(newMessageList);
    }

    // Scroll to new local message
    const newestMessage = incomingMessages[incomingMessages.length - 1];
    if (isLocalMessage(newestMessage)) {
      scrollToLastMessage();
    }
  }, []);

  useEffect(() => {
    const messageList = incomingMessages ? Object.values(incomingMessages) : null;
    const validMessageList = messageList?.filter(item => !disappearListId.includes(item.id));
    if (Array.isArray(validMessageList) && validMessageList.length > 0) {
      handleIncomingMessage(validMessageList, groupDetail.id, selectedThreadId);
    }
  }, [incomingMessages]);

  useEffect(() => {
    if (selectedThreadId) {
      handleGetParentMessage();
    }
  }, [selectedThreadId]);

  // Handle restore delete only me
  useEffect(() => {
    const { message, isDelete } = deleteOnlyMeMsgRedux;
    const newMessageList = messageListRef.current;
    const showMessageIds = newMessageList.map(item => item.id);

    if (message && message.threadId === selectedThreadId && !isDelete && showMessageIds.includes(message.id)) {
      const deleteMessageIndex = newMessageList.findIndex(item => item.id === message.id);
      const deleteMessage = newMessageList[deleteMessageIndex];
      newMessageList[deleteMessageIndex] = {
        ...deleteMessage,
        isDeleteOnlyMe: false,
        state: SystemConstant.STATE.active,
        modified: Date.now(),
      };
      setMessageList(newMessageList);
    }
  }, [deleteOnlyMeMsgRedux]);

  const isShowParent = showMessage.length === 0 || scrollDataRef.current.isOldest;
  return (
    <InfiniteScroll
      id={CHAT_WRAPPER_ID}
      className={classes.wrapChatItem}
      hasMore={isLoadMore}
      isReverse={true}
      ScrollTooltip={ScrollTooltip}
      onScrollToTop={getNewerMessage}
      onScrollToBottom={getOlderMessage}
      onClickScrollButton={scrollToLastMessage}
    >
      {/* Reply message */}
      {showMessage.map(item => (
        <MessageItem key={item.id} message={item} isThreadMode={true} />
      ))}

      {isShowParent && (
        <>
          {showMessage.length > 0 && (
            <LineChat
              data={getLabel(LangConstant.FM_NUMBER_THREAD_REPLY, {
                number: showMessage.filter(item => !item.isDeleteAll).length,
              })}
            />
          )}

          {/* Parent message */}
          <MessageItem message={parentMessage} isThreadMode={true} />
        </>
      )}
    </InfiniteScroll>
  );
};

ThreadViewMode.propTypes = {
  messages: PropTypes.array,
  groupMembers: PropTypes.array,
  groupDetail: PropTypes.object,

  onSendMessage: PropTypes.func,
};

ThreadViewMode.defaultProps = {};

export default memo(ThreadViewMode);

const useStyles = makeStyles({
  wrapChatItem: {
    display: "flex",
    flexDirection: "column-reverse",
    height: "100%",
    padding: "0 22px",
    paddingBottom: 15,
  },
});
